import React, { useState } from "react";
import { Share2, Facebook, Twitter, MessageCircle, Send, Link2, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { socialLinks, canNativeShare, nativeShare as share, copyText } from "@/lib/share";

export default function ShareTrack({ track: tr, size = "md", align = "end" }) {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);
  if (!tr) return null;

  const url = `${window.location.origin}/radio?track=${tr.id}&lang=${lang}`;
  const shareUrl = `${window.location.origin}/api/share/radio/${tr.id}?lang=${lang}`;
  const text = t("radio_share_text")
    .replace("{title}", tr.title).replace("{artist}", tr.artist)
    .replace("{country}", tr.country.short_names[lang] || tr.country.short_names.en);
  const links = socialLinks(shareUrl, text);

  const track = (method) => trackEvent("share", { method, content_type: "track", item_id: tr.id, lang });

  const items = [
    { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: links.whatsapp, cls: "bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25" },
    { id: "facebook", label: "Facebook", Icon: Facebook, href: links.facebook, cls: "bg-[#1877F2]/15 text-[#5ea2ff] hover:bg-[#1877F2]/25" },
    { id: "twitter", label: "X / Twitter", Icon: Twitter, href: links.twitter, cls: "bg-sand/10 text-sand hover:bg-sand/20" },
    { id: "telegram", label: "Telegram", Icon: Send, href: links.telegram, cls: "bg-[#229ED9]/15 text-[#4fc3f7] hover:bg-[#229ED9]/25" },
  ];

  const copyLink = async () => {
    track("copy_link");
    await copyText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const nativeShare = () => { track("native"); share({ title: tr.title, text, url: shareUrl }); };

  const btn = size === "sm" ? "w-9 h-9" : "w-10 h-10";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label={t("radio_share")}
          title={t("radio_share")}
          className={`${btn} rounded-full bg-sand/10 hover:bg-sand/20 text-sand flex items-center justify-center transition-colors`}
          data-testid="share-track-trigger"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} side="top" className="w-72 p-4 rounded-2xl border-sand/20 bg-ink text-sand" data-testid="share-track-popover">
        <div className="text-[10px] uppercase tracking-widest text-sand/60 mb-1">{t("radio_share")}</div>
        <div className="text-sm font-semibold truncate mb-3" dir="auto">{tr.title} · {tr.artist}</div>
        <div className="grid grid-cols-2 gap-2">
          {items.map(({ id, label, Icon, href, cls }) => (
            <a key={id} href={href} target="_blank" rel="noopener noreferrer" onClick={() => track(id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${cls}`}
              data-testid={`share-track-${id}`}>
              <Icon className="w-4 h-4" /> {label}
            </a>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-sand/15 bg-sand/5 px-3 py-2">
          <span className="flex-1 truncate text-xs text-sand/60" dir="ltr" data-testid="share-track-url">{url}</span>
          <button onClick={copyLink} aria-label={copied ? t("copied") : "Copy link"}
            className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium ${copied ? "text-oasis" : "text-ochre hover:text-sand"}`}
            data-testid="share-track-copy">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied && t("copied")}
          </button>
        </div>
        {canNativeShare() && (
          <button onClick={nativeShare} className="mt-2 w-full py-2 rounded-xl bg-ochre text-white text-sm font-medium hover:bg-ochre/90 transition-colors" data-testid="share-track-native">
            {t("share")}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
